import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Loader,
  Select,
  TextInput
} from '@mantine/core';
import { AlertCircle } from 'tabler-icons-react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import PageView from '../../common/PageView';
import PageCard from '../../common/PageCard';
import PageFilter from '../../common/PageFilter';
import PaginationList from '../../common/PaginationList';
import AppFlexbox from '../../common/AppFlexbox';
import {
  useGlobalFormState,
  useModalState,
  usePaginationFilter
} from '../../../helpers/hooks';
import AppAnchor from '../../common/AppAnchor';
import { singularPluralFormat } from '../../../helpers/format';
import { triggerNotification } from '../../../helpers/notification';
import ExternalProductSelectItem from './ExternalProductSelectItem';
import { getFromStorage, saveToStorage } from '../../../helpers/storage';
import { ECOM_PRODUCT_STATUS_ENUM } from '../../../config/constants';
import ExternalProductImportModal from './ExternalProductImportModal';

const ExternalProductImportView = ({ config }) => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const { state: authState } = useContext(AuthContext);
  const { state, fetchExternalProducts, importExternalProducts } = useContext(
    VendorContext
  );
  const [filterState, setFilterState] = useState({
    externalId: null,
    search: '',
    pageIndex: 1,
    hideAddedProducts: false,
    loading: false,
    fetchingMore: false
  });

  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const {
    formState,
    isSubmitting,
    setFormState,
    resetFormState,
    submitFormState
  } = useGlobalFormState(
    {
      externalId: null,
      existingConnections: [],
      selectedProducts: [],
      switchToBulkEditor: true,
      isPrivate: false,
      setProductToLive: false
    },
    {
      confirmDiscard: false,
      containerWidth: 1050,
      saveButtonText: 'Import products',
      hideDiscardButton: true,
      getIsVisible: (data) => data.selectedProducts.length > 0,
      skipExitOnUnsavedChanges: true
    }
  );
  const {
    pageIndex,
    totalCount,
    data: externalProducts,
    filter,
    onFilterChange,
    onPageChange,
    onReset
  } = usePaginationFilter(
    { externalId: null },
    (newFilter) => {
      fetchExternalProducts(
        authState.pkEcomVendor,
        {
          fkEcomExternalConnectionType: config?.pkEcomExternalConnectionType,
          externalId:
            filterState.externalId ||
            getFromStorage(
              `external_id_${config?.pkEcomExternalConnectionType}`
            ) ||
            null,
          includeOptions: !filterState.externalId,
          ...newFilter
        },
        (data) => {
          saveToStorage(
            `external_id_${config?.pkEcomExternalConnectionType}`,
            data.paginatedExternalProducts.externalId?.toString()
          );
          setFilterState({
            ...filterState,
            externalId: data.paginatedExternalProducts.ecomExternalConnectionExternalId?.toString(),
            existingConnections: data.ecomVendorProductConnections,
            loading: false,
            fetchingMore: false
          });
        },
        (e) => {
          triggerNotification(e);
          setFilterState({
            ...filterState,
            loading: false,
            fetchingMore: false
          });
        }
      );
    },
    state.paginatedExternalProducts
  );

  const loading = !config || !hasFetched.current || filterState.loading;
  const hasFilters = !!filterState.search;
  const filteredProducts = externalProducts.filter(
    (product) =>
      (!filterState.search ||
        product.name
          .toLowerCase()
          .includes(filterState.search.toLowerCase())) &&
      (!filterState.hideAddedProducts ||
        !formState.existingConnections.some(
          (c) => c.productExternalId === product.productExternalId
        ))
  );
  const allProductsSelected =
    !loading &&
    filteredProducts.every((p) =>
      formState.selectedProducts.some(
        (sp) => sp.productExternalId === p.productExternalId
      )
    );

  useEffect(() => {
    if (authState.pkEcomVendor && config) {
      setFilterState({ ...filterState, loading: true });
      onReset();
      hasFetched.current = true;
    }
    else if (!config) {
      navigate(`/vendor/products`);
    }
  }, [authState.pkEcomVendor]);

  useEffect(() => {
    if (filterState.externalId !== formState.externalId) {
      resetFormState({
        externalId: filterState.externalId,
        existingConnections: filterState.existingConnections,
        selectedProducts: [],
        switchToBulkEditor: true,
        isPrivate: false,
        setProductToLive: false
      });
    }
  }, [filterState.externalId]);

  const onFetchMore = () => {
    setFilterState({ ...filterState, fetchingMore: true });
    onPageChange(pageIndex + 1);
  };

  const onImportProducts = () => {
    submitFormState((formData, onErrorCallback) => {
      importExternalProducts(
        authState.pkEcomVendor,
        {
          ecomExternalConnectionExternalId: formData.externalId,
          fkEcomExternalConnectionType: config.pkEcomExternalConnectionType,
          products: formData.selectedProducts.map((p) => ({
            productExternalId: p.productExternalId,
            isPrivate: formData.isPrivate,
            fkEcomProductStatus: formState.setProductToLive
              ? ECOM_PRODUCT_STATUS_ENUM.ACTIVE
              : null
          }))
        },
        (d) => {
          triggerNotification('Products imported!', 'Success', 'green');
          resetFormState();

          if (formData.switchToBulkEditor) {
            navigate(
              `/vendor/products/bulk-edit?ids=${d.map((p) => p.uuid).join(',')}`
            );
          }
        },
        onErrorCallback
      );
    });
  };

  return (
    <PageView
      backPathDefault="/vendor/products"
      loading={!config}
      rightSection={
        config?.products?.dashboardUrl && (
          <Button
            color="dark"
            component="a"
            href={config?.products?.dashboardUrl}
            radius="md"
            size="xs"
            style={{ flex: 1 }}
            target="_blank"
            variant="light"
          >
            View in {config?.title}
          </Button>
        )
      }
      title={`Import from ${config?.title}`}
    >
      <AppStack style={{ gap: 16 }}>
        <AppStack style={{ gap: 8 }}>
          <PageCard style={{ padding: 8 }}>
            <AppFlexbox style={{ gap: 8 }}>
              <AppStack>
                <AlertCircle color="dodgerblue" style={{ size: 16 }} />
              </AppStack>

              <AppText style={{ fontSize: 14 }}>
                Select your desired products from {config?.title} and click the
                "Import" button to seamlessly add them to your vendor.
              </AppText>
            </AppFlexbox>
          </PageCard>

          <PageFilter
            inputs={[
              {
                key: 1,
                Component: (
                  <TextInput
                    onChange={(e) => {
                      setFilterState({
                        ...filterState,
                        search: e.currentTarget.value,
                        pageIndex: 1
                      });
                    }}
                    placeholder="Search products..."
                    style={{ flex: 2 }}
                    value={filterState.search}
                  />
                )
              },
              ...(config?.products?.filterOptions.map((op) => ({
                key: op.optionField,
                Component: (
                  <Select
                    clearable
                    data={
                      state.externalProductOptions.value
                        .find((v) => v.optionField === op.optionField)
                        ?.values.map((s) => ({
                          value: s.value,
                          label: s.label
                        })) || []
                    }
                    disabled={loading}
                    onChange={(v) => {
                      if (
                        (v || !op.required) &&
                        filterState[op.requestField] !== v
                      ) {
                        setFilterState({
                          ...filterState,
                          [op.requestField]: v,
                          pageIndex: 1,
                          loading: true
                        });
                        onFilterChange({ ...filter, [op.requestField]: v });
                      }
                    }}
                    placeholder="Select shop"
                    searchable
                    style={{ flex: 1 }}
                    value={filterState[op.requestField] || ''}
                  />
                )
              })) || [])
            ]}
          />

          <PageCard style={{ padding: 8 }}>
            <AppStack style={{ gap: 8 }}>
              <AppStack style={{ gap: 5 }}>
                <AppFlexbox
                  style={{
                    gap: 8,
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                    Available products
                  </AppText>
                  <AppFlexbox>
                    <Checkbox
                      checked={filterState.hideAddedProducts || false}
                      disabled={loading || isSubmitting}
                      label="Hide added products"
                      onChange={() => {
                        setFilterState({
                          ...filterState,
                          hideAddedProducts: !filterState.hideAddedProducts
                        });
                      }}
                    />
                  </AppFlexbox>
                </AppFlexbox>
                <AppFlexbox
                  style={{
                    gap: 8,
                    justifyContent: 'space-between'
                  }}
                >
                  <AppFlexbox>
                    <Checkbox
                      checked={allProductsSelected || false}
                      disabled={
                        loading || filteredProducts.length === 0 || isSubmitting
                      }
                      label="Select all"
                      onChange={() => {
                        setFormState({
                          ...formState,
                          selectedProducts: [
                            ...formState.selectedProducts.filter(
                              (p) =>
                                !filteredProducts.some(
                                  (fp) =>
                                    fp.productExternalId === p.productExternalId
                                )
                            ),
                            ...(allProductsSelected
                              ? []
                              : filteredProducts.map((p) => ({productExternalId: p.productExternalId})))
                          ]
                        });
                      }}
                    />
                  </AppFlexbox>
                  {!loading && (
                    <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                      <AppStack
                        style={{
                          justifyContent: 'end',
                          alignItems: 'end',
                          gap: 0
                        }}
                      >
                        <AppFlexbox style={{ alignItems: 'center', gap: 8 }}>
                          {filterState.fetchingMore ? (
                            <>
                              <Loader color="dark" size={16} />
                              <AppText style={{ fontSize: 14, color: '#666' }}>
                                products
                              </AppText>
                            </>
                          ) : (
                            <AppText style={{ fontSize: 14, color: '#666' }}>
                              {totalCount} products
                            </AppText>
                          )}
                        </AppFlexbox>

                        {externalProducts.length < totalCount && (
                          <AppAnchor
                            disabled={filterState.fetchingMore}
                            onClick={onFetchMore}
                          >
                            Fetch more
                          </AppAnchor>
                        )}
                      </AppStack>
                    </AppFlexbox>
                  )}
                </AppFlexbox>
              </AppStack>
              <AppStack style={{ gap: 8 }}>
                {!loading && filteredProducts.length === 0 ? (
                  <AppStack
                    style={{
                      border: 'solid 1px #ced4da',
                      padding: 20,
                      gap: 8,
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      No products {hasFilters ? 'found by search' : 'available'}
                    </AppText>

                    {externalProducts.length < totalCount && (
                      <>
                        <AppFlexbox style={{ gap: 8 }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              color: '#666',
                              fontStyle: 'italic'
                            }}
                          >
                            Not all products are fetched yet.{' '}
                            {!filterState.fetchingMore && (
                              <AppAnchor
                                disabled={filterState.fetchingMore}
                                onClick={onFetchMore}
                              >
                                Fetch more
                              </AppAnchor>
                            )}
                          </AppText>
                          {filterState.fetchingMore && (
                            <Loader
                              color="dark"
                              size={16}
                              style={{ minWidth: 62 }}
                            />
                          )}
                        </AppFlexbox>

                        {hasFilters && (
                          <Divider
                            label="OR"
                            labelPosition="center"
                            style={{ width: '100%', maxWidth: 300 }}
                          />
                        )}
                      </>
                    )}
                    {hasFilters && (
                      <Button
                        color="grey"
                        onClick={() =>
                          setFilterState({ ...filterState, search: '' })
                        }
                        size="compact-sm"
                        variant="outline"
                      >
                        Clear filters
                      </Button>
                    )}
                  </AppStack>
                ) : (
                  <PaginationList
                    emptyMessage="No products available"
                    isLoading={loading}
                    items={
                      filteredProducts
                        ?.sort((a, b) =>
                          (a.name ?? '').localeCompare(b.name ?? '')
                        )
                        .map((p) => {
                          const productFormState = formState.selectedProducts.find(
                            (sp) => sp.productExternalId === p.productExternalId
                          );
                          const selected = !!productFormState;

                          return (
                            <ExternalProductSelectItem
                              key={p.productExternalId}
                              config={config}
                              disabled={isSubmitting}
                              isAddedToVendor={formState.existingConnections.some(
                                (c) =>
                                  c.productExternalId === p.productExternalId
                              )}
                              onAction={(action) => {
                                onOpenModal(action, p);
                              }}
                              onSelect={() => {
                                setFormState({
                                  ...formState,
                                  selectedProducts: selected
                                    ? formState.selectedProducts.filter(
                                        (sp) =>
                                          sp.productExternalId !==
                                          p.productExternalId
                                      )
                                    : [
                                        ...formState.selectedProducts,
                                        {productExternalId: p.productExternalId}
                                      ]
                                });
                              }}
                              onViewDetails={() => {}}
                              product={p}
                              selected={selected}
                            />
                          );
                        }) || []
                    }
                    itemsPerPage={10}
                    LoadingComponent={ExternalProductSelectItem}
                    loadingComponentCount={10}
                    onPageChange={(nextPageIndex) =>
                      setFilterState({
                        ...filterState,
                        pageIndex: nextPageIndex
                      })
                    }
                    pageIndex={filterState.pageIndex}
                  />
                )}
              </AppStack>
            </AppStack>
          </PageCard>
        </AppStack>
        <AppFlexbox
          style={{ flex: 1, alignItems: 'end', justifyContent: 'end' }}
        >
          <Button
            color="dark"
            disabled={formState.selectedProducts.length === 0}
            id="save-button"
            loading={isSubmitting}
            onClick={() => {
              onOpenModal('confirm_import');
            }}
            size="compact-sm"
            variant="filled"
          >
            {formState.selectedProducts.length > 0
              ? `Import ${singularPluralFormat(
                  formState.selectedProducts.length,
                  'product',
                  'products'
                )}`
              : 'Import products'}
          </Button>
        </AppFlexbox>
      </AppStack>

      <ExternalProductImportModal
        config={config}
        externalProducts={formState.selectedProducts}
        formState={formState}
        isLoading={isSubmitting}
        isOpen={modalState.isOpen && modalState.action === 'confirm_import'}
        onClose={onCloseModal}
        onSubmit={onImportProducts}
        setFormState={setFormState}
      />
    </PageView>
  );
};

ExternalProductImportView.propTypes = { config: PropTypes.object.isRequired };

export default ExternalProductImportView;
